import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import DescriptionModeSelect from '@cohort/merchants/apps/common/DescriptionModeSelect';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {defaultMedia, parseDefaults} from '@cohort/merchants/apps/common/triggers/utils';
import {getDescriptionMode} from '@cohort/merchants/apps/common/triggers/utils';
import ConnectionPicker from '@cohort/merchants/components/connections/ConnectionPicker';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import Separator from '@cohort/merchants/components/Separator';
import {useConnectionProfile} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {TwitterMerchantConnectorStruct} from '@cohort/shared/apps/twitter/merchantConnector';
import type {TwitterFollowTriggerStruct} from '@cohort/shared/apps/twitter/triggers/follow';
import type {Language} from '@cohort/shared/schema/common';
import {TWITTER_URL} from '@cohort/shared-frontend/common/apps/twitter';
import {Fragment, useEffect, useRef} from 'react';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {
      en: `Follow us on X`,
      fr: `Suivez-nous sur X`,
      es: `Sigue a nosotros en X`,
      de: `Folgen Sie uns auf X`,
    },
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const TwitterFollowTriggerConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<TwitterFollowTriggerStruct>
> = ({formReturn: {setValue, control, watch, reset, register}, trigger, step}) => {
  const init = useRef(false);
  const merchant = useCurrentMerchant();

  const [definedLanguages, connectionId] = watch(['definedLanguages', 'trigger.connectionId']);

  const {data: profile} = useConnectionProfile<TwitterMerchantConnectorStruct>(
    merchant.id,
    connectionId ?? '',
    {enabled: !!connectionId && init.current}
  );

  useEffect(() => {
    if (profile) {
      const defaults = getDefaults(definedLanguages);
      setValue('defaultTitle', defaults.title);
      setValue('defaultDescription', defaults.description);
      setValue(
        'trigger.triggerIntegrationConfig.profileUrl',
        `${TWITTER_URL}/${profile.properties.username}`
      );
    }
  }, [setValue, profile, definedLanguages]);

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);
    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: getDescriptionMode(definedLanguages, defaults, step),
      title: step?.title ?? defaults.title,
      description: step?.description ?? defaults.description,
      icon: step?.icon !== undefined ? step.icon : '🐦',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: trigger?.connectionId ?? null,
        triggerIntegrationId: 'twitter.follow' as const,
        triggerIntegrationConfig: {
          profileUrl: trigger?.triggerIntegrationConfig.profileUrl ?? '',
        },
      },
      statementEnabled: false,
    }));
    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <div className="max-w-sm">
        <ConnectionPicker
          appId="twitter"
          control={control}
          register={register}
          connectionIdFieldName="trigger.connectionId"
        />
      </div>
      <DescriptionModeSelect />
      <LanguageSelectorInput />
      <StepTitleInput />
      <Separator />
      <StepContentInput />
    </Fragment>
  );
};

export default TwitterFollowTriggerConfigComponent;
